import React from 'react'
import { Box, Card, Chip, LinearProgress, Stack, Tooltip, Button } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import FlagIcon from '@mui/icons-material/Flag'
import GroupsIcon from '@mui/icons-material/Groups'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WTypography from '../../../Components/whiteTypography'

/**
 * LAP-768 — Sections visuelles du Conseiller Social / Relationnel.
 * Miroir de `ProAdvisorSections` (LAP-767), palette relationnelle (rose) :
 *   - SocialScoreCard          : score global 0-100 + détail par dimension
 *   - SocialDiagnosticPanel    : forces / faiblesses / risques + recommandations
 *   - SocialActionsPanel       : actions prioritaires (avec route Life)
 *   - SocialBreakdownPanel     : répartition des interactions par cercle
 *   - SocialRecommendationPanel: insight mis en avant (le cas échéant)
 */

const SOCIAL_ACCENT = '#f472b6'
const CLAUDE_ORANGE = '#d97757'

const SCORE_COLORS = {
  good: '#4ade80',
  warning: '#fbbf24',
  critical: '#f87171',
}
const NEUTRAL_COLOR = '#94a3b8'

function scoreColor(score) {
  if (score == null) return NEUTRAL_COLOR
  if (score >= 70) return SCORE_COLORS.good
  if (score >= 45) return SCORE_COLORS.warning
  return SCORE_COLORS.critical
}

// ─────────────────────────────────────────────────────────────────────────
// Score
// ─────────────────────────────────────────────────────────────────────────

export function SocialScoreCard({ score, totals = {}, userFirstName }) {
  const { t } = useTranslation('layout')
  const color = scoreColor(score.global)

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #0a162e 0%, #11264a 100%)',
        border: '1px solid rgba(244,114,182,0.18)',
        borderRadius: 3,
        p: { xs: 2.5, md: 4 },
        color: 'white',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <WTypography variant="overline" sx={{ color: '#f3b3d4 !important', letterSpacing: 1.5 }}>
            {t('conseillerSocial.score.overline', { defaultValue: 'Diagnostic relationnel' })}
          </WTypography>
          <WTypography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerSocial.score.title', {
              defaultValue: `Votre vie relationnelle, ${userFirstName || ''}`,
              name: userFirstName,
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#e6d3e0 !important', mt: 1 }}>
            {t('conseillerSocial.score.subtitle', {
              defaultValue:
                'Une note synthétique calculée sur votre activité relationnelle, l’entretien de vos relations clés, la diversité de votre réseau, votre équilibre pro / perso et votre dynamique.',
            })}
          </WTypography>
          {typeof score.evaluatedCount === 'number' &&
            score.evaluatedCount < (score.totalCount || 5) && (
              <WTypography
                variant="caption"
                sx={{ color: '#f3b3d4 !important', mt: 1, display: 'block' }}
              >
                {t('conseillerSocial.score.coverage', {
                  defaultValue: `Score calculé sur ${score.evaluatedCount}/${
                    score.totalCount || 5
                  } volets — les autres sont « Non évalué » faute de données. Ajoutez vos contacts et vos interactions pour les activer.`,
                  evaluated: score.evaluatedCount,
                  total: score.totalCount || 5,
                })}
              </WTypography>
            )}
          <Stack direction="row" gap={1} flexWrap="wrap" mt={1.5}>
            {totals.totalContacts > 0 && (
              <Chip
                icon={<GroupsIcon sx={{ color: '#f3b3d4 !important' }} />}
                label={t('conseillerSocial.score.contactsChip', {
                  defaultValue: `${totals.totalContacts} contacts`,
                  count: totals.totalContacts,
                })}
                sx={{
                  bgcolor: 'rgba(244,114,182,0.14)',
                  color: '#f7c8e0',
                  border: '1px solid rgba(244,114,182,0.3)',
                  fontWeight: 700,
                }}
              />
            )}
            {totals.interactionsThisMonth > 0 && (
              <Chip
                size="small"
                icon={<FavoriteBorderIcon sx={{ color: '#9be3cf !important' }} />}
                label={t('conseillerSocial.score.interactionsChip', {
                  defaultValue: `${totals.interactionsThisMonth} interactions ce mois-ci`,
                  count: totals.interactionsThisMonth,
                })}
                sx={{
                  bgcolor: 'rgba(90,209,176,0.15)',
                  color: '#9be3cf',
                  fontWeight: 700,
                }}
              />
            )}
            {totals.ratioPerso != null && (
              <Chip
                size="small"
                label={t('conseillerSocial.score.balanceChip', {
                  defaultValue: `Perso : ${totals.ratioPerso}% · Pro : ${totals.ratioPro}%`,
                  perso: totals.ratioPerso,
                  pro: totals.ratioPro,
                })}
                sx={{
                  bgcolor: 'rgba(167,139,250,0.15)',
                  color: '#cbb9f7',
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>
        </Box>
        <ScoreCircle value={score.global} color={color} t={t} />
      </Box>

      <WTypography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#f3d6e6 !important' }}>
        {t('conseillerSocial.score.dimensionsTitle', { defaultValue: 'Détail par volet' })}
      </WTypography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' }}
        gap={2}
      >
        {score.dimensions.map((d) => (
          <DimensionRow key={d.key} dim={d} t={t} />
        ))}
      </Box>
    </Card>
  )
}

function ScoreCircle({ value, color, t }) {
  const size = 150
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const hasValue = typeof value === 'number' && Number.isFinite(value)
  const offset = circumference * (1 - (hasValue ? value : 0) / 100)

  return (
    <Box position="relative" width={size} height={size}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
        />
      </svg>
      <Box
        position="absolute"
        top={0}
        left={0}
        width={size}
        height={size}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <WTypography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>
          {hasValue ? value : '—'}
        </WTypography>
        <WTypography sx={{ fontSize: 13, color: '#f3b3d4 !important' }}>
          {hasValue
            ? t('conseillerSocial.score.outOf100', { defaultValue: '/ 100' })
            : t('conseillerSocial.score.noData', { defaultValue: 'non évalué' })}
        </WTypography>
      </Box>
    </Box>
  )
}

function DimensionRow({ dim, t }) {
  const available = dim.available !== false
  const color = available ? scoreColor(dim.score) : NEUTRAL_COLOR
  const statusColor = available ? SCORE_COLORS[dim.status] || NEUTRAL_COLOR : NEUTRAL_COLOR
  const notEvaluatedLabel = t('conseillerSocial.score.notEvaluated', {
    defaultValue: 'Non évalué',
  })
  const tooltipText = available
    ? [dim.detail, dim.dataSource].filter(Boolean).join(' ')
    : dim.emptyHint || dim.detail

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
          cursor: 'help',
          transition: 'all .2s',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', transform: 'translateY(-2px)' },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <WTypography variant="body2" sx={{ fontWeight: 600 }}>
            {dim.label}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{
              color: `${color} !important`,
              fontWeight: 700,
              fontSize: available ? undefined : 12,
            }}
          >
            {available ? dim.score : notEvaluatedLabel}
          </WTypography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={available ? dim.score : 0}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box width={6} height={6} borderRadius="50%" bgcolor={statusColor} />
            <WTypography variant="caption" sx={{ color: '#f3b3d4 !important' }}>
              {t('conseillerSocial.score.weightLabel', {
                defaultValue: `pondération ${Math.round(dim.weight * 100)} %`,
                weight: Math.round(dim.weight * 100),
              })}
            </WTypography>
          </Box>
        </Box>
        {(dim.dataSource || dim.emptyHint) && (
          <WTypography
            variant="caption"
            sx={{
              color: '#c79bb4 !important',
              display: 'block',
              mt: 0.75,
              lineHeight: 1.4,
            }}
          >
            {available ? dim.dataSource : dim.emptyHint}
          </WTypography>
        )}
      </Box>
    </Tooltip>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Diagnostic
// ─────────────────────────────────────────────────────────────────────────

export function SocialDiagnosticPanel({ diagnostic, recommendations = [] }) {
  const { t } = useTranslation('layout')
  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <WTypography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('conseillerSocial.diagnostic.title', {
          defaultValue: 'Forces, faiblesses, risques',
        })}
      </WTypography>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
        <DiagColumn
          icon={<CheckCircleOutlineIcon sx={{ color: SCORE_COLORS.good }} />}
          color={SCORE_COLORS.good}
          title={t('conseillerSocial.diagnostic.strengths', { defaultValue: 'Forces' })}
          items={diagnostic.strengths}
          empty={t('conseillerSocial.diagnostic.strengthsEmpty', {
            defaultValue:
              'Pas encore de volet solide identifié — entretenez vos relations et diversifiez votre réseau pour en faire apparaître.',
          })}
        />
        <DiagColumn
          icon={<WarningAmberIcon sx={{ color: SCORE_COLORS.warning }} />}
          color={SCORE_COLORS.warning}
          title={t('conseillerSocial.diagnostic.weaknesses', { defaultValue: 'Faiblesses' })}
          items={diagnostic.weaknesses}
          empty={t('conseillerSocial.diagnostic.weaknessesEmpty', {
            defaultValue: 'Aucun volet en demi-teinte — bel équilibre relationnel.',
          })}
        />
        <DiagColumn
          icon={<ReportProblemOutlinedIcon sx={{ color: SCORE_COLORS.critical }} />}
          color={SCORE_COLORS.critical}
          title={t('conseillerSocial.diagnostic.risks', { defaultValue: 'Risques' })}
          items={diagnostic.risks}
          empty={t('conseillerSocial.diagnostic.risksEmpty', {
            defaultValue: 'Aucun volet critique pour le moment. Continuez ainsi.',
          })}
        />
      </Box>

      {diagnostic.notEvaluated && diagnostic.notEvaluated.length > 0 && (
        <Box
          mt={2.5}
          sx={{
            p: 1.75,
            borderRadius: 2,
            bgcolor: 'rgba(148,163,184,0.08)',
            border: '1px solid rgba(148,163,184,0.25)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <HelpOutlineIcon sx={{ color: NEUTRAL_COLOR, fontSize: 18 }} />
            <WTypography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: `${NEUTRAL_COLOR} !important` }}
            >
              {t('conseillerSocial.diagnostic.notEvaluated', {
                defaultValue: 'Volets non évalués',
              })}
            </WTypography>
          </Box>
          <WTypography
            variant="caption"
            sx={{ color: '#f3b3d4 !important', display: 'block', mb: 1 }}
          >
            {t('conseillerSocial.diagnostic.notEvaluatedHint', {
              defaultValue:
                'Faute de données, ces volets ne comptent pas dans le score. Renseignez-les pour les activer.',
            })}
          </WTypography>
          <Stack gap={0.75}>
            {diagnostic.notEvaluated.map((it) => (
              <Box key={it.key}>
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {it.title}
                </WTypography>
                <WTypography variant="caption" sx={{ color: '#f3b3d4 !important' }}>
                  {it.detail}
                </WTypography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {recommendations.length > 0 && (
        <Box mt={3}>
          <WTypography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('conseillerSocial.diagnostic.recoTitle', {
              defaultValue: 'Recommandations personnalisées',
            })}
          </WTypography>
          <Stack gap={1}>
            {recommendations.map((r, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(244,114,182,0.10)',
                  border: '1px solid rgba(244,114,182,0.25)',
                }}
              >
                <WTypography variant="body2" sx={{ color: '#f3d6e6 !important' }}>
                  {r}
                </WTypography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  )
}

function DiagColumn({ icon, color, title, items, empty }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: `1px solid ${color}33`,
        minHeight: 140,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.25}>
        {icon}
        <WTypography variant="subtitle2" sx={{ fontWeight: 700, color: `${color} !important` }}>
          {title}
        </WTypography>
      </Box>
      {items.length === 0 ? (
        <WTypography variant="body2" sx={{ color: '#f3b3d4 !important', fontStyle: 'italic' }}>
          {empty}
        </WTypography>
      ) : (
        <Stack gap={0.75}>
          {items.map((it) => (
            <Box key={it.key}>
              <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                {it.title}
              </WTypography>
              <WTypography variant="caption" sx={{ color: '#f3b3d4 !important' }}>
                {it.detail}
              </WTypography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Priority actions
// ─────────────────────────────────────────────────────────────────────────

const IMPACT_COLORS = {
  high: '#f87171',
  medium: '#fbbf24',
  low: '#4ade80',
}

export function SocialActionsPanel({ actions }) {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  if (!actions || actions.length === 0) return null

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <WTypography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('conseillerSocial.actions.title', { defaultValue: 'Actions prioritaires' })}
      </WTypography>
      <Stack gap={1.5}>
        {actions.map((a) => (
          <Box
            key={a.id}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <FlagIcon sx={{ color: IMPACT_COLORS[a.impact] || '#94a3b8', fontSize: 18 }} />
                <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {a.title}
                </WTypography>
                <Chip
                  size="small"
                  label={t(`conseillerSocial.actions.impact.${a.impact}`, {
                    defaultValue: a.impact,
                  })}
                  sx={{
                    bgcolor: `${IMPACT_COLORS[a.impact] || '#94a3b8'}22`,
                    color: IMPACT_COLORS[a.impact] || '#94a3b8',
                    fontWeight: 700,
                    height: 22,
                  }}
                />
              </Box>
              <WTypography variant="body2" sx={{ color: '#e6d3e0 !important' }}>
                {a.description}
              </WTypography>
            </Box>
            {a.route && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(a.route)}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#f7c8e0',
                  borderColor: 'rgba(244,114,182,0.45)',
                  '&:hover': { bgcolor: 'rgba(244,114,182,0.12)' },
                }}
              >
                {t('conseillerSocial.actions.cta', { defaultValue: 'Y aller' })}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Breakdown — répartition des interactions par cercle
// ─────────────────────────────────────────────────────────────────────────

export function SocialBreakdownPanel({ breakdown }) {
  const { t } = useTranslation('layout')
  const entries = (breakdown?.entries || []).filter((e) => e.value > 0)
  if (entries.length === 0) return null

  const maxValue = entries.reduce((m, e) => Math.max(m, e.value), 0) || 1

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexWrap="wrap"
        gap={1}
      >
        <WTypography variant="h6" sx={{ fontWeight: 700 }}>
          {t('conseillerSocial.breakdown.title', {
            defaultValue: 'Répartition de vos interactions par cercle',
          })}
        </WTypography>
        <WTypography variant="caption" sx={{ color: '#f3b3d4 !important' }}>
          {t('conseillerSocial.breakdown.total', {
            defaultValue: `Total : ${breakdown.total} interactions`,
            total: breakdown.total,
          })}
        </WTypography>
      </Box>

      <Stack gap={1.5}>
        {entries.map((row) => (
          <Box key={row.key}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: row.color,
                  }}
                />
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.category}
                </WTypography>
              </Box>
              <WTypography variant="caption" sx={{ color: '#f3b3d4 !important' }}>
                {t('conseillerSocial.breakdown.row', {
                  defaultValue: `${row.value} · ${row.percent} %`,
                  value: row.value,
                  percent: row.percent,
                })}
              </WTypography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(row.value / maxValue) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 4 },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Recommandation mise en avant (insight backend)
// ─────────────────────────────────────────────────────────────────────────

export function SocialRecommendationPanel({ recommendation }) {
  const { t } = useTranslation('layout')
  if (!recommendation || !recommendation.trim()) return null

  return (
    <Card
      sx={{
        bgcolor: 'rgba(217,119,87,0.08)',
        border: '1px solid rgba(217,119,87,0.25)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <AutoAwesomeIcon sx={{ color: CLAUDE_ORANGE }} />
        <WTypography variant="h6" sx={{ fontWeight: 700 }}>
          {t('conseillerSocial.recommendation.title', {
            defaultValue: 'Recommandation de votre conseiller',
          })}
        </WTypography>
      </Box>
      <WTypography
        variant="body2"
        sx={{ color: '#f4e3da !important', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
      >
        {recommendation}
      </WTypography>
    </Card>
  )
}

export { SOCIAL_ACCENT, CLAUDE_ORANGE }
